import {
    FormComponentValue,
    FormControlProps,
    FormElementControlPropsType,
} from 'components/common/form/layout/control';
import { FormComponentBuilder } from 'components/common/form/layout/index';
import { alpha, Box, Button, FormLabel, IconButton, Stack, Theme } from '@mui/material';
import { Params as UrlParams } from '@remix-run/router/dist/utils';
import { useQuery } from '@tanstack/react-query';
import { instance } from 'api/api';
import { APIResponse } from 'tools/types';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import useCustomSimplifiedForm from 'components/common/form/hooks/useCustomSimplifiedForm';
import { TemplateFormComponent } from 'components/common/form/layout/ReactHookFormController';
import IconMi from 'components/common/icon/IconMi';
import useBundleTranslation from 'i18n';

interface ListItem {
    i: number;
    column: string;
    color: string;
}

export interface FormChartColumnsListProps extends FormControlProps {
    urlParams: UrlParams;
    columnsUrl: string;
}

export default function FormChartColumnsList({
    controlProps,
    elementProps,
}: FormElementControlPropsType<FormChartColumnsListProps>) {
    const { t } = useBundleTranslation('form');
    // Get allowed columns from the API
    const { data: allowedColumns } = useQuery<Array<FormComponentValue>, Error>({
        queryKey: ['FormChartColumnsList', controlProps.urlParams, controlProps.columnsUrl],
        queryFn: async () => {
            const response = await instance.get<APIResponse<Array<FormComponentValue>>>(controlProps.columnsUrl, {
                params: controlProps.urlParams,
            });
            return response.data.status == 'OK' ? response.data.data : [];
        },
    });
    const totalColumns = useMemo(() => [{ value: '', label: '--' }].concat(allowedColumns ?? []), [allowedColumns]);

    const nextIndex = useRef(0);
    const [currentValue, setCurrentValue] = useState<Array<ListItem>>([]);
    useEffect(() => {
        let defaultValue: any = controlProps.value;
        if (typeof defaultValue == 'string') {
            try {
                defaultValue = JSON.parse(defaultValue);
            } catch (e) {
                const newValue: ListItem = {
                    column: '',
                    color: '#FFFFFF',
                    i: nextIndex.current++,
                };
                defaultValue = [newValue];
            }

            const list = defaultValue.map((item: any) => ({ ...item, i: nextIndex.current++ }));
            setCurrentValue(list);
        }
    }, []);

    const handleRowChange = (index: number, row: ListItem) => {
        const newValue = [...currentValue];
        newValue[index] = row;
        setCurrentValue(newValue);
        controlProps.onChange(JSON.stringify(newValue));
    };

    const handleDelete = (rowIndex: number) => {
        const index = currentValue.findIndex((item) => item.i == rowIndex);
        if (index == -1) {
            return;
        }
        const newValue = [...currentValue];
        newValue.splice(index, 1);
        setCurrentValue(newValue);
        controlProps.onChange(JSON.stringify(newValue));
    };

    const handleAddItem = () => {
        const newRow: ListItem = {
            column: '',
            color: '#FFFFFF',
            i: nextIndex.current++,
        };
        setCurrentValue([...currentValue, newRow]);
        controlProps.onChange(JSON.stringify([...currentValue, newRow]));
    };

    return (
        <Box>
            <Stack direction={'column'} spacing={1}>
                {currentValue.map((row, index) => (
                    <Row
                        isSingle={currentValue.length == 1}
                        key={row.i}
                        row={row}
                        columns={totalColumns}
                        onChange={(newRow) => handleRowChange(index, newRow)}
                        onDelete={() => handleDelete(row.i)}
                    />
                ))}
                <Box sx={{ width: '150px' }}>
                    <Button onClick={() => handleAddItem()} startIcon={<IconMi icon={'new'} />} variant={'light'}>
                        {t('add_item')}
                    </Button>
                </Box>
            </Stack>
        </Box>
    );
}

function Row({
    row,
    columns,
    isSingle,
    onChange,
    onDelete,
}: {
    row: ListItem;
    columns: Array<FormComponentValue>;
    isSingle: boolean;
    onChange: (row: ListItem) => void;
    onDelete: () => void;
}) {
    const { t } = useBundleTranslation('form');
    const form = useCustomSimplifiedForm(row);
    const watch = form.form.hookFormWatch();
    useEffect(() => {
        if (JSON.stringify(watch) !== JSON.stringify(row)) {
            onChange(watch);
        }
    }, [watch]);

    return (
        <Stack
            direction="row"
            alignContent={'center'}
            p={1}
            spacing={1}
            sx={{ border: (theme: Theme) => `1px solid ${alpha(theme.palette.text.primary, 0.08)}` }}
        >
            <Stack direction="column" spacing={1} width={'100%'}>
                <Box>
                    <Box>
                        <FormLabel className={'design-variant--secondary'}>{t('dataset_column')}</FormLabel>
                    </Box>
                    <TemplateFormComponent
                        componentValues={columns}
                        component={'FormSelect'}
                        elementProps={form}
                        name={'column'}
                        designVariant={'secondary'}
                    />
                </Box>
                <Box>
                    <Box>
                        <FormLabel className={'design-variant--secondary'}>{t('color')}</FormLabel>
                    </Box>
                    <TemplateFormComponent
                        elementProps={form}
                        component={'ColorPicker'}
                        name={'color'}
                        props={{ autoFocus: true }}
                        designVariant={'secondary'}
                    />
                </Box>
            </Stack>
            {!isSingle && (
                <Box>
                    <IconButton sx={{ padding: 0 }} onClick={() => onDelete()}>
                        <IconMi icon="times" fontSize="16" />
                    </IconButton>
                </Box>
            )}
        </Stack>
    );
}

export class FormChartColumnsListBuilder extends FormComponentBuilder {
    prepareProps(): FormChartColumnsListProps {
        return {
            ...this.controlProps,
            urlParams: {
                ...this.elementProps.urlParams,
                ...this.elementProps.componentProps?.urlParams,
            } as UrlParams,
            columnsUrl: this.elementProps.componentProps?.columnsUrl ?? '',
        };
    }
}
